import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { updateProfile, type ProfileUpdatePayload, type UserMe } from "@/api/profile";
import { profileQuery } from "@/lib/appQueries";
import { markOnboardingComplete } from "@/lib/onboarding";

export const PROFILE_QUERY_KEY = profileQuery.queryKey;

/**
 * The account, read by the gate, the preferences provider and the welcome
 * guide.
 *
 * `retryOnMount: false` because three components observe this one query and
 * React Query restarts a failed query for each new observer that mounts. Every
 * restart clears the error and reports `pending` again, which is the signal the
 * gate used to answer by rendering nothing (BEN-141 QF8). One failure should be
 * reported once and retried when the user asks, not once per component that
 * happens to appear. A navigation still warms it through `useAppDataPrefetch`,
 * and the notice's own Try again still calls `refetch()`.
 */
export function useProfile(options?: { enabled?: boolean }) {
  return useQuery({
    ...profileQuery,
    enabled: options?.enabled ?? true,
    retryOnMount: false,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileUpdatePayload) => updateProfile(payload),
    onSuccess: (data: UserMe) => {
      // Write the server's authoritative response straight into the cache so
      // the OnboardingGate sees fresh data on the next render — no refetch, no
      // bounce back to /onboarding while a stale cache is in flight.
      queryClient.setQueryData(PROFILE_QUERY_KEY, data);
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => updateProfile({ profile: { onboarding_complete: true } }),
    onSuccess: (data: UserMe) => {
      queryClient.setQueryData(PROFILE_QUERY_KEY, data);
      // Seed the localStorage optimistic hint so a hard refresh (which clears
      // the in-memory cache) starts the gate as "complete" instead of bouncing
      // through /onboarding while the cold profile fetch is pending.
      markOnboardingComplete();
    },
  });
}
